import React, { useState, useMemo, useEffect } from "react";
import { PreferredZoneType } from "@foodi/core";
import { Text, View, StyleSheet, TouchableOpacity, Dimensions, ScrollView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { I18n } from "react-redux-i18n";
import { GlobalState } from "@redux";
import { useDevices } from "@hooks";
import { Spacing, Colors } from "@constants";
import { PreferredZonesThunks } from "@modules";
import { Heart } from "@assets";
import { Button } from "@atomic";
import { arraySortByName } from "@utils";

export interface PreferredZoneProps {
	idHoldingView?: string;
	preferredZones: PreferredZoneType[];
	maximumPreferredZones: number;
  zones: PreferredZoneType[];
  onClose: (props?: any) => void;
  modalHeight?: number;
  shouldUpdateZones?: boolean
  onPreferredZoneSelect?: (props?: any) => void;
  setDisableModalClose?: (props?: any) => void;
  isForcedModal?: boolean;
  isFullWidth?: boolean;
}

const MINIMUM_PREFERRED_ZONES = 1;

const zoneItem = (item: any, index: number, styles: any, isSelected: boolean, onSelect: any) => {
	return (
		<TouchableOpacity
			key={`PreferedZones_${index}_${item}`}
			style={[styles.zoneCard, isSelected && styles.zoneCardSelected]}
			onPress={() => onSelect(item)}
		>
        { isSelected ? (
          <View style={styles.iconContainer}>
            <Heart />
          </View>
        ): (
          <Text style={styles.plusIcon}>+</Text>
        )}
			<Text style={isSelected ? [styles.zoneCardText, styles.zoneCardTextSelected] : styles.zoneCardText} numberOfLines={1} ellipsizeMode="tail">
				{item.name}
			</Text>
		</TouchableOpacity>
	);
};

export const PreferredZone = React.memo(({
  idHoldingView, 
  preferredZones, 
  zones, 
  modalHeight, 
  maximumPreferredZones, 
  shouldUpdateZones = false,
  onPreferredZoneSelect = () => {},
  onClose = () => {},
  setDisableModalClose = () => {},
  isForcedModal = false,
  isFullWidth = true
}: PreferredZoneProps) => {
  const dispatch = useDispatch();

  const [isMobile] = useDevices();
  const brandTheme = useSelector(
    (state: GlobalState) => state.brandTheme.brandTheme)
  
  const styles = useMemo(() => _styles(isMobile, modalHeight, brandTheme, isFullWidth), [isMobile, modalHeight, brandTheme, isFullWidth]);

  const [selectedZones, setSelectedZones] = useState<any[]>(preferredZones);

  useEffect(() => {
    setSelectedZones(preferredZones)
  }, [preferredZones])

  const [disableSubmit, setDisableSubmit] = useState<boolean>(
    preferredZones.length < MINIMUM_PREFERRED_ZONES
  );

  const onSelect = (zoneToSelect: PreferredZoneType) => {
    const selectedIndex = selectedZones.findIndex(({ name }) => name === zoneToSelect.name);
    let tempZones = [...selectedZones];

    if (selectedIndex !== -1) {
      tempZones.splice(selectedIndex, 1);
    } else {
      tempZones.push(zoneToSelect);
    }

    setSelectedZones(tempZones);

    const shouldDisableSubmit = tempZones.length < MINIMUM_PREFERRED_ZONES || tempZones.length > maximumPreferredZones;

    setDisableSubmit(shouldDisableSubmit);
    setDisableModalClose(shouldDisableSubmit);
  };

  const onSubmit = async () => {
    try {
      // this part is used inside the homepage filters

      const sortedZones = selectedZones.sort(arraySortByName);

      if (shouldUpdateZones) {
        const updatedZones: any[] = [];

        // check which zones were deselected as preferred zones
        preferredZones.forEach(({name}) => {
          const foundZone = selectedZones.findIndex(eachSelectedZone => eachSelectedZone.name == name);

          if(foundZone === -1){
            updatedZones.push({name});
          }

        })

        // check which zones are new preferred zones
        selectedZones.forEach(({name}) => {
          const foundZone = preferredZones.findIndex(eachSelectedZone => eachSelectedZone.name == name);
          
          if(foundZone === -1){
            updatedZones.push({name});
          }

        })
        onPreferredZoneSelect({updatedZones, updatedPreferredZones: selectedZones});

        dispatch(PreferredZonesThunks.updatePreferredZones({
          input: {
            idHoldingView,
            preferredZones: sortedZones.map(({name}) => ({name})),
          }
        }));

      }

      onClose(sortedZones);
    } catch (e) {
      console.error(`ERROR - updatePreferredZones - ${e}`);
    }
  };

  const infoMessage = maximumPreferredZones === MINIMUM_PREFERRED_ZONES ? 
    I18n.t('preferredZones.minimumZones', { minZones: MINIMUM_PREFERRED_ZONES }) :
    I18n.t('preferredZones.maxZones', { minZones: MINIMUM_PREFERRED_ZONES, maxZones: maximumPreferredZones });

  const title = isForcedModal
    ? I18n.t(
        maximumPreferredZones === 1
          ? 'preferredZones.forcedModal.titleSingular'
          : 'preferredZones.forcedModal.titlePlural'
      )
    : I18n.t('preferredZones.modals.title');

  const description = isForcedModal
    ? I18n.t(
        maximumPreferredZones === 1
          ? 'preferredZones.forcedModal.descriptionSingular'
          : 'preferredZones.forcedModal.descriptionPlural'
      )
    : I18n.t('preferredZones.modals.subTitle');

	return (
		<View style={styles.mainContainer}>
			<View style={styles.header}>
				<Text style={styles.title}>{title}</Text>
        <Text style={styles.subTitle}>{description}</Text>
        <Text style={styles.infoMessage}>
          {infoMessage}
        </Text>
			</View>
      <ScrollView style={{flexGrow: 1}}>
        <View style={styles.cardsContainer}>
          {zones?.map((eachZone, index) => {
            const isZoneSelected =
              selectedZones.findIndex(({ name }) => name === eachZone.name) !== -1;

            return zoneItem(eachZone, index, styles, isZoneSelected, onSelect);
          })}
        </View>
      </ScrollView>
      
      <Button
        onPress={onSubmit}
        label={I18n.t("preferredZones.modals.validateBtn")}
        styleButton={styles.submitBtn}
        disabled={disableSubmit}
      />
		</View>
	);
});

const _styles = (isMobile: boolean, modalHeight?: number, brandTheme?: any, isFullWidth?: boolean) => 
  StyleSheet.create({
    mainContainer: {
      padding: Spacing.L,
      paddingBottom: isMobile ? 0 : Spacing.L,
      alignItems: "center",
      height: modalHeight ? modalHeight : "100%",
      width: "100%",
    },
    header: {
      textAlign: "center",
      marginBottom: 5,
    },
    description: {
      textAlign: "center",
      fontFamily: "Manrope-Bold",
      fontSize: 13,
      color: Colors.foodiBlack,
      marginTop: 12,
    },
    infoMessage: {
      fontFamily: "Manrope",
      fontSize: 13,
      color: Colors.foodiBlack,
      marginTop: 5,
    },
    touchableView: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    title: {
      fontFamily: "Manrope-Bold",
      fontSize: 22,
      lineHeight: 30,
      color: Colors.foodiBlack,
      textAlign: "center",
    },
    subTitle: {
      textAlign: "center",
      fontFamily: "Manrope-Bold",
      fontSize: 13,
      color: Colors.foodiBlack,
      marginTop: 12,
    },
    cardsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: "100%",
      justifyContent: isMobile ? 'flex-start' : "center",
    },
    firstZone: {
      backgroundColor: Colors.foodiDefault,
    },
    nextZone: {
      textAlign: "center",
      fontFamily: "Manrope-Bold",
      fontSize: 13,
      borderColor: Colors.foodiDefault,
      borderRadius: 8,
      width: 100,
      padding: 8,
      borderWidth: 1,
    },
    zoneCardText: {
      color: Colors.foodiBlack,
      fontFamily: "manrope",
      fontSize: 16,
    },
    zoneCardTextSelected: {
      color: brandTheme.buttonActionColors?.textColor || Colors.foodiBlack,
    },
    closeXButtonStyle: {
      top: -15,
      right: -5,
    },
    modalStyle: {
      width: 414,
      maxHeight: 650,
    },
    submitBtn: {
      bottom: 0,
      marginBottom: 0,
      marginTop: 10,
      width: isMobile && isFullWidth ? Dimensions.get('window').width : '200px',
      alignSelf: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      height: 60,
      borderRadius: isMobile && isFullWidth ? 0 : 8,
    },
    zoneCard: {
      maxWidth: isMobile ? "90%" : 300,
      height: 40,
      borderRadius: 8,
      borderWidth: 0.5,
      paddingVertical: 5,
      paddingHorizontal: 15,
      marginTop: 10,
      marginRight: 10,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row'
    },
    zoneCardSelected: {
      backgroundColor: brandTheme.buttonActionColors?.backgroundColor || Colors.foodiDefault,
      borderWidth: 0,
    },
    plusIcon: {
      color: Colors.foodiBlack,
      fontFamily: "manrope-bold",
      fontSize: 30,
      marginRight: 5,
      lineHeight: 30
    },
    iconContainer: {
      marginRight: 5,
      color: brandTheme.buttonActionColors?.textColor || Colors.foodiBlack,
    }
});

